/**
 * The company the SPA believes the session is on, readable outside React.
 *
 * Why this exists: an act-as in another tab, its expiry or a rule that no longer allows it all move the session to
 * another company without this page knowing. The Apollo auth link sends this id as `x-expected-company-id`, and the
 * backend refuses a request whose session is now elsewhere, so a form still showing one company never writes to
 * another.
 */
let expectedCompanyId: string | undefined;

/**
 * Recorded by the viewer provider each time the viewer's company changes, and moved ahead of the viewer on an act-as:
 * the viewer refetch that follows must already name the new company, or the backend refuses it.
 */
export function setExpectedCompanyId(companyId: string | undefined): void {
  expectedCompanyId = companyId;
}

/** Read by the Apollo auth link on every request. */
export function getExpectedCompanyId(): string | undefined {
  return expectedCompanyId;
}

type SessionCompanyChangedListener = () => void;

let sessionCompanyChangedListener: SessionCompanyChangedListener | undefined;

/**
 * Lets the viewer provider, which can reset the store, reconnect the socket and navigate, react to a refusal the
 * Apollo error link sees outside React. One listener: there is one viewer provider.
 */
export function onSessionCompanyChanged(listener: SessionCompanyChangedListener): () => void {
  sessionCompanyChangedListener = listener;

  return () => {
    if (sessionCompanyChangedListener === listener) {
      sessionCompanyChangedListener = undefined;
    }
  };
}

/**
 * Called by the Apollo error link when the backend refused a request because the session moved company. Ignored while
 * the page is already moving company: requests sent for the company being left are refused on purpose, and the reset
 * in progress reloads everything anyway, so a second reset and a second toast would only repeat it.
 */
export function notifySessionCompanyChanged(): void {
  if (companyChangeInProgress) {
    return;
  }
  sessionCompanyChangedListener?.();
}

let companyChangeInProgress = false;

/** Whether an act-as, a stop or a reset after a refusal is moving the page onto another company right now. */
export function isCompanyChangeInProgress(): boolean {
  return companyChangeInProgress;
}

/** The side effects a company reset drives, injected so the sequence reads and tests without React. */
export type CompanySessionResetSteps = {
  /** Drops every cached object of the previous company and refetches the queries still active. */
  resetStore: () => Promise<unknown>;
  /** Lands on the page the new company should open on. */
  navigate: (path: string) => void;
  /** Reopens the socket, whose handshake still names the previous company. */
  reconnect: () => void;
  /** Hides the routed pages while true, so no page query runs against the wrong company. */
  setInProgress: (inProgress: boolean) => void;
};

export type CompanySessionResetOptions = {
  /** Keep the current page instead of landing on `/`. A management page holds no company data, so it can stay. */
  stayOnPage?: boolean;
  /** Where to land instead of `/`: the page the act-as started from, when it exists in every company. */
  landingPath?: string;
};

/** Apollo rejects a refetch with a DOM `AbortError` when the query it belongs to is torn down while in flight. */
function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Moves the page onto the session's new company. The routed pages are hidden for the whole reset: a page still
 * mounted would refetch its queries against the new company (not found errors, flashes of the old page), and the
 * previous page would otherwise stay on screen while the home page's chunk loads.
 */
export async function resetCompanySession(
  steps: CompanySessionResetSteps,
  nextCompanyId: string | undefined,
  options?: CompanySessionResetOptions,
): Promise<void> {
  companyChangeInProgress = true;
  steps.setInProgress(true);
  // Before the reset: the viewer refetch must name the company the session is now on, not the one it just left.
  // Undefined when that company is unknown (a refusal from another tab), and the refetch then goes without a header.
  setExpectedCompanyId(nextCompanyId);
  try {
    try {
      await steps.resetStore();
    } catch (error) {
      // A query torn down while its refetch was in flight does not matter any more; anything else is a real failure.
      if (!isAbortError(error)) {
        throw error;
      }
    }
    // After the refetch, not before: the home route picks Root's landing from the viewer, which must be the new one.
    if (!options?.stayOnPage) {
      steps.navigate(options?.landingPath ?? '/');
    }
    steps.reconnect();
  } finally {
    companyChangeInProgress = false;
    steps.setInProgress(false);
  }
}

/** Per tab: sessionStorage outlives a reload but not the tab, and each tab can sit in its own company. */
const COMPANY_CHOSEN_KEY = 'altitude:company-chosen';

/**
 * Records that the user picked a company in this tab. A Root lands in management by default, but once they chose a
 * company (even their own, which shows no banner) `/` must show that company's app, not bounce them back.
 */
export function markCompanyChosen(): void {
  try {
    window.sessionStorage.setItem(COMPANY_CHOSEN_KEY, '1');
  } catch {
    // Storage blocked (private mode, sandboxed frame): Root simply keeps landing in management.
  }
}

/** Whether the user picked a company in this tab since signing in. */
export function hasChosenCompany(): boolean {
  try {
    return window.sessionStorage.getItem(COMPANY_CHOSEN_KEY) === '1';
  } catch {
    return false;
  }
}

/** Forgets the choice on sign-out and on a fresh sign-in, so a new session always lands Root in management. */
export function clearCompanyChosen(): void {
  try {
    window.sessionStorage.removeItem(COMPANY_CHOSEN_KEY);
  } catch {
    // Nothing stored to forget when storage is blocked.
  }
}
